import React from "react";
import { Stack } from "@mui/material";
import useJiraSetupApi from "src/scenes/integrations/jira-setup/use-setup-api";
import SelectProjectsAutocomplete from "src/scenes/integrations/jira-setup/projects-autocomplete";
import { LoadingButton } from "@mui/lab";
import request from "src/utils/request";
import { ToastContext } from "src/components/providers/toast-provider";
import EnvironmentSelect from "src/components/environment-select";

const JiraSetup = () => {
    const [loading, setLoading] = React.useState(false);
    const [loadingSetup, setLoadingSetup] = React.useState(true);
    const [projects, setProjects] = React.useState([]);
    const [projectNames, setProjectNames] = React.useState([]);
    const [projectsToSetup, setProjectsToSetup] = React.useState([]);

    const { setMessage, setSeverity, setIsToastTriggered } = React.useContext(ToastContext);

    const jiraSetupApi = useJiraSetupApi();

    React.useEffect(() => {
        if (jiraSetupApi && jiraSetupApi.status && jiraSetupApi.status === "SUCCESS") {
            setProjects(jiraSetupApi.data);
            const namesArray = jiraSetupApi.data.map((project) => project.name);
            setProjectNames(namesArray);
            setLoadingSetup(false);
            setProjectsToSetup(
                jiraSetupApi.data
                    .filter((project) => project.adadotFields.length)
                    .map(({ fields, ...project }) => {
                        const bugsField = project.adadotFields.find(
                            (field) => field.adadotName === "bugs_env"
                        );
                        return {
                            ...project,
                            fields: bugsField
                                ? {
                                      id: bugsField.jiraId,
                                      name: bugsField.jiraName,
                                      adadotName: bugsField.adadotName,
                                  }
                                : {},
                        };
                    })
            );
        }
    }, [jiraSetupApi.status]);

    const handleSaveSetup = async () => {
        setLoading(true);
        const createSetupArray = () => {
            return projectsToSetup.map(({ id, fields }) => {
                const project = projects.find((proj) => proj.id === id);
                return {
                    jiraId: fields.id,
                    jiraName: fields.name,
                    adadotName: "bugs_env",
                    projectId: project.id,
                };
            });
        };
        const setupArray = { fields: createSetupArray() };
        try {
            await request(`/api/jira/setup`, {
                method: "POST",
                body: JSON.stringify(setupArray),
            });
            setMessage(`Jira setup saved successfully.`);
            setSeverity("success");
            setIsToastTriggered(true);
            setLoading(false);
        } catch {
            setLoading(false);
            setMessage(`Failed to save Jira setup. Please try again.`);
            setSeverity("warning");
            setIsToastTriggered(true);
        }
    };

    const validateFields = () => {
        return projectsToSetup.some((item) => Object.keys(item.fields).length === 0);
    };

    const findByProjectName = ({ name }) => {
        return projects.find((x) => x.name === name);
    };

    return (
        <Stack
            direction="column"
            style={{ width: "100%" }}
            mb={4}
            pr={5}
            spacing={3}
            alignItems="flex-end"
        >
            <SelectProjectsAutocomplete
                data={jiraSetupApi && jiraSetupApi.data}
                projectNames={projectNames}
                projectsToSetup={projectsToSetup}
                setProjectsToSetup={setProjectsToSetup}
                projects={projects}
                loading={loadingSetup}
            />
            {projectsToSetup &&
                projectsToSetup.map((project) => {
                    return (
                        <Stack
                            direction="row"
                            key={project.name}
                            spacing={2}
                            style={{ width: "100%" }}
                            justifyContent="space-between"
                        >
                            <p>{project.name}</p>
                            <EnvironmentSelect
                                fields={findByProjectName({ name: project.name }).fields}
                                envField={findByProjectName({
                                    name: project.name,
                                }).adadotFields.find((field) => field.adadotName === "bugs_env")}
                                projectsToSetup={projectsToSetup}
                                setProjectsToSetup={setProjectsToSetup}
                                projectName={project.name}
                            />
                        </Stack>
                    );
                })}
            <LoadingButton
                variant="contained"
                size="medium"
                disabled={projectsToSetup.length === 0 || validateFields()}
                loading={loading}
                style={{ width: "158px" }}
                onClick={() => {
                    handleSaveSetup();
                }}
            >
                Save Setup
            </LoadingButton>
        </Stack>
    );
};

export default JiraSetup;
